import type { ReactNode } from "react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

export type MvpStatus = "lite-later" | "pro" | "future";

interface Props {
  status: MvpStatus;
  title: string;
  description?: string;
  /** Bullet list of features that will arrive — shown to users in Vietnamese. */
  upcoming?: string[];
  actions?: ReactNode;
}

const HEADLINE: Record<MvpStatus, string> = {
  "lite-later": "Tính năng đang được hoàn thiện",
  pro: "Tính năng dành cho gói Pro",
  future: "Sẽ có trong phiên bản 1.5",
};

const SUBLINE: Record<MvpStatus, string> = {
  "lite-later":
    "Chức năng này nằm trong gói Lite và sẽ ra mắt ở bản cập nhật tiếp theo.",
  pro:
    "Tính năng này không nằm trong gói Lite. Nâng cấp lên Addy HIS Pro để sử dụng.",
  future:
    "Tính năng này được lên kế hoạch cho phiên bản 1.5. Hiện tại chưa khả dụng.",
};

const PILL_TONE: Record<MvpStatus, BadgeProps["variant"]> = {
  "lite-later": "secondary",
  pro: "default",
  future: "muted",
};

const PILL_TEXT: Record<MvpStatus, string> = {
  "lite-later": "Sắp ra mắt",
  pro: "Gói Pro",
  future: "Phiên bản 1.5",
};

/**
 * Polished Vietnamese placeholder used in HIS Lite for pages whose backend is
 * still on the way (`lite-later`), Pro-only (`pro`), or scheduled for v1.5
 * (`future`). Replaces the dev-only {@code PageScaffold} skeleton so users
 * always see a clean, professional state — never the developer placeholder.
 */
export function MvpStatusCard({
  status,
  title,
  description,
  upcoming,
  actions,
}: Props) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-bold text-foreground">
            {title}
            <Badge variant={PILL_TONE[status]}>{PILL_TEXT[status]}</Badge>
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>

      <Card className="max-w-3xl">
        <CardContent className="space-y-2 p-6">
          <h2 className="text-lg font-semibold text-foreground">
            {HEADLINE[status]}
          </h2>
          <p className="text-sm text-muted-foreground">{SUBLINE[status]}</p>
          {upcoming && upcoming.length > 0 && (
            <div className="space-y-2 pt-2">
              <h3 className="text-sm font-medium text-foreground">
                Những gì sẽ có trong trang này
              </h3>
              <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {upcoming.map((u, i) => (
                  <li key={i}>{u}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
